import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface UseAccountStatusReturn {
  status: string | null;
  isPaused: boolean;
  loading: boolean;
}

export function useAccountStatus(): UseAccountStatusReturn {
  const { user } = useAuth();
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Track if we've fetched once to avoid loading flash on refetch.
  // When loading=true, ProtectedRoute shows a spinner and UNMOUNTS children,
  // which destroys all React state (wizard step, form data, etc.).
  const hasFetchedOnce = useRef(false);

  useEffect(() => {
    async function fetchStatus() {
      if (!user?.id) {
        setStatus(null);
        setLoading(false);
        return;
      }

      // Only show loading spinner on initial fetch, not on refetches
      if (!hasFetchedOnce.current) {
        setLoading(true);
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("status")
          .eq("user_id", user.id)
          .single();

        if (error) {
          console.error("Error fetching account status:", error);
          // Default to 'active' on error to avoid blocking users
          setStatus("active");
        } else {
          // Default to 'active' if status is null (backwards compatible)
          setStatus(data?.status ?? "active");
        }
      } catch (err) {
        console.error("Unexpected error fetching account status:", err);
        setStatus("active");
      } finally {
        setLoading(false);
        hasFetchedOnce.current = true;
      }
    }

    fetchStatus();
  }, [user?.id]);

  return {
    status,
    isPaused: status === "paused",
    loading,
  };
}
